import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import {
  Mail,
  Newspaper,
  Download,
  ShieldCheck,
  Home,
  ChevronRight,
} from "lucide-react";

const SITE = "https://heallexa.com";

export type PressArticleMeta = {
  slug: string;
  title: string;
  outlet: string;
  date: string;
  excerpt: string;
};

export const ARTICLES: PressArticleMeta[] = [
  {
    slug: "end-healthcare-booking-crisis",
    title:
      "How Heallexa is Using WhatsApp and SMS to End the Healthcare Booking Crisis",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa replaces legacy doctor portals with WhatsApp, SMS, and voice AI scheduling across 6.3M records.",
  },
  {
    slug: "disrupt-doctor-directories",
    title:
      "New AI Health Discovery Platform Heallexa Goes Live, Disrupting Traditional Doctor Directories",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa launches a zero-fee discovery network with WhatsApp, SMS, and voice AI booking.",
  },
  {
    slug: "zero-fee-ai-directory-launch",
    title:
      "Heallexa Launches World's Largest AI Healthcare Directory with 6.3 Million Records, Eliminating Per-Appointment Booking Fees for Providers",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa debuts a zero-fee provider directory with WhatsApp, SMS, and voice AI booking.",
  },
  {
    slug: "launch-6m-providers",
    title:
      "Heallexa Launches AI Powered Healthcare Search Platform With a Directory of More Than 6.3 Million Provider Profiles",
    outlet: "Heallexa Newsroom",
    date: "2026-05-30",
    excerpt:
      "Dallas, Texas, Heallexa unveils its AI powered healthcare search platform with 6.3M+ provider profiles.",
  },
];

export const Route = createFileRoute("/press")({
  head: () => ({
    meta: [
      { title: "Press Room | Heallexa" },
      {
        name: "description",
        content:
          "Heallexa press room, media coverage, company information, leadership bios, brand assets, and press contact.",
      },
      { property: "og:title", content: "Press Room | Heallexa" },
      {
        property: "og:description",
        content:
          "Media coverage, company facts, and press contact for Heallexa, the AI powered healthcare search platform.",
      },
      { property: "og:url", content: `${SITE}/press` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/press` }],
  }),
  component: PressLayout,
});

export function formatPressDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}, ${y}`;
}

function PressLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isOverview = pathname === "/press" || pathname === "/press/";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <header className="pt-32 md:pt-36 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1200px] px-6">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Heallexa Newsroom
          </p>
          <h1 className="text-[clamp(2.25rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-[-0.04em] text-foreground">
            Press &amp; Media Resources
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-[560px]">
            Coverage, company information, and assets for journalists writing about Heallexa, the
            AI powered healthcare search platform trusted by patients and providers nationwide.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="mailto:press@heallexa.com"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
            >
              <Mail className="h-4 w-4" /> Contact Press Team
            </a>
            <Link
              to="/press"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors rounded-md"
            >
              <Download className="h-4 w-4" /> Brand Assets
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px rounded-md overflow-hidden border border-border bg-border">
            {[
              { k: "6.3M+", v: "Provider profiles" },
              { k: "50", v: "U.S. states" },
              { k: "AI", v: "Search experience" },
              { k: "2026", v: "Founded" },
            ].map((s) => (
              <div key={s.v} className="bg-card px-5 py-4">
                <div className="text-2xl font-semibold text-foreground tracking-tight tabular-nums">{s.k}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Body: sidebar + outlet */}
      <div className="mx-auto max-w-[1200px] px-6 py-16 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        <Reveal from="up" delay={60} as="div" className="lg:sticky lg:top-20 lg:self-start space-y-5">
          <div className="card-surface-flat rounded-md overflow-hidden">
            <Link
              to="/press"
              className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors ${
                isOverview ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
              }`}
            >
              <Home className="h-4 w-4" />
              Press Room Overview
            </Link>

            <div className="border-t border-border px-4 py-3 flex items-center gap-2">
              <Newspaper className="h-3.5 w-3.5 text-primary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Articles
              </p>
            </div>

            <ul className="divide-y divide-border">
              {ARTICLES.map((a) => {
                const href = `/press/${a.slug}`;
                const active = pathname === href || pathname === `${href}/`;
                return (
                  <li key={a.slug}>
                    <Link
                      to={href}
                      className={`group block w-full text-left px-4 py-3.5 transition-colors ${
                        active ? "bg-primary/5" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                            active ? "text-primary" : "text-muted-foreground"
                          }`}
                        >
                          {a.outlet}
                        </p>
                        <time className="text-[10px] text-muted-foreground tabular-nums">
                          {formatPressDate(a.date)}
                        </time>
                      </div>
                      <p
                        className={`mt-1.5 text-sm font-medium leading-snug line-clamp-3 ${
                          active ? "text-foreground" : "text-foreground/90 group-hover:text-foreground"
                        }`}
                      >
                        {a.title}
                      </p>
                      <div
                        className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${
                          active ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                        }`}
                      >
                        Read article <ChevronRight className="h-3 w-3" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="card-surface-flat rounded-md p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Quick Facts
              </p>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <Row k="Founded" v="2026" />
              <Row k="Category" v="AI Healthcare Search" />
              <Row k="Headquarters" v="Dallas, TX" />
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Website</dt>
                <dd>
                  <a href={SITE} className="text-primary hover:underline font-medium">
                    heallexa.com
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </Reveal>

        <main className="min-w-0">
          <div className="card-surface rounded-md p-6 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>

      <FooterSection />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-3 last:border-0 last:pb-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-foreground font-semibold text-right">{v}</dd>
    </div>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div>
      <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">{eyebrow}</p>
      <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
        {title}
      </h2>
      {subtitle && <p className="mt-3 text-[14px] text-muted-foreground max-w-[520px]">{subtitle}</p>}
    </div>
  );
}
